import _ from 'lodash'
import { Module } from 'vuex'
import {
  createAdvertiser,
  fetchAdvertiserInfo,
  getAdvertiserOptions,
  updateAdvertiser,
} from '@/api/advertiser/manage'
import { RootState } from '../types'
import { AdvertiserInvoice } from '@/types/advertiser/index.type'
import { AdvertiserAccountType } from '@/common/permission'

// https://confluence.mobvista.com/pages/viewpage.action?pageId=49642221
export interface AdvertiserBasicInfoState {
  name: string
  email: string
  contact_num: string
  contact: string
  organization: string
  timezone: string
  country: string
  company_address: string
  remark: string
  mws_account_type: AdvertiserAccountType
  cc_email: string
}
export interface AdvertiserExpandInfoState {
  bd_id: number | ''
  bd_am_id: number | ''
  pm_adv_id: number | ''
  account_type: number
  currency: number
  cooperation_entity: number | ''
  advance: number
  password: string
}
export interface AdvertiserInfoState {
  id: number | ''
  basic: AdvertiserBasicInfoState
  expand: AdvertiserExpandInfoState
  invoices: AdvertiserInvoice[]
}

export interface AdvCooperationEntity {
  value: string
  label: string
  currency: number
}
export type AdvertiserOptionsState = null | {
  [key: string]: {
    [key: string]: string | number
    label: string
    value: string
  }[]
}
export interface AdvertiserState {
  info: AdvertiserInfoState
  verifyDialog: {
    title: string
    visible: boolean
  }
  dialog: {
    title: string
    visible: boolean
  }
  type: string
  exists: number | string
  loading: boolean
  options: AdvertiserOptionsState
  advertiser_name: string
  advertiser_email: string
}

const getInvoice = (): AdvertiserInvoice => {
  return {
    name: '',
    company: '',
    address: '',
  }
}

const getInfo = (): AdvertiserInfoState => {
  return {
    id: '',
    basic: {
      name: '',
      mws_account_type: AdvertiserAccountType.Normal,
      email: '',
      contact_num: '',
      contact: '',
      timezone: '',
      organization: '',
      country: '',
      company_address: '',
      remark: '',
      cc_email: '',
    },
    expand: {
      bd_id: '',
      bd_am_id: '',
      pm_adv_id: '',
      account_type: 1,
      currency: 1,
      cooperation_entity: '',
      advance: 1,
      password: '',
    },
    invoices: [getInvoice()],
  }
}

const namespaced = true
const state: AdvertiserState = {
  info: getInfo(),
  // 校验邮箱弹窗
  verifyDialog: {
    title: '新建广告主',
    visible: false,
  },

  dialog: {
    title: '新增广告主',
    visible: false,
  },

  type: 'add',
  exists: '', // 校验邮箱的状态
  loading: false,

  options: null,
  // 账号中心-广告主名称
  advertiser_name: '',
  // 账号中心-广告主邮箱
  advertiser_email: '',
}

// adv_cooperation_entity: [{k: 1, v: "Mintegral International Limited", currency: 1},…]
// bd: [{k: 10729, v: "adn2", v1: "adn2"}, {k: 11479, v: "cplbd", v1: "cplbd"},…]
// bd_am: [{k: 10473, v: "alan", v1: "alan"}, {k: 10475, v: "kelly(王颖慧)", v1: "kelly", v2: "王颖慧"},…]
// bd_am_team: [{k: "BD-adn", v: "BD-adn"}, {k: "BD-CPL", v: "BD-CPL"}, {k: "BD-CPR", v: "BD-CPR"},…]
// bd_team: [{k: "BD-adn", v: "BD-adn"}, {k: "BD-CPL", v: "BD-CPL"}, {k: "BD-CPR", v: "BD-CPR"},…]
// pm_adv: [{k: 10714, v: "jiamin(莫嘉敏)", v1: "jiamin", v2: "莫嘉敏"},…]
// pm_adv_team: [{k: "PM-adn", v: "PM-adn"}, {k: "PM-CPL", v: "PM-CPL"}, {k: "PM-CPR", v: "PM-CPR"},…]

const advertiser: Module<AdvertiserState, RootState> = {
  namespaced,
  state,

  mutations: {
    updateLoading(state, value) {
      state.loading = value
    },
  },

  actions: {
    async getOptions({ state }, useCache = true) {
      if (useCache && !_.isEmpty(state.options)) return state.options
      const res = await getAdvertiserOptions()
      const options: {
        [key: string]: {
          [key: string]: string | number
          k: string
          v: string
        }[]
      } | null = _.get(res, 'data.data')
      const newOptions: AdvertiserOptionsState = {}
      if (options) {
        _.each(options, (optItem, key) => {
          newOptions[key] = (optItem || []).map((item) => {
            const newItem = {
              ...item,
              label: item.v, // v 转换为 label
              value: item.k, // k 转换为 value
            }
            _.omit(newItem, ['v', 'k'])
            return newItem
          })
        })
        state.options = newOptions
      }
      return state.options
    },

    openVerifyDialog({ state }) {
      state.verifyDialog = {
        ...state.verifyDialog,
        visible: true,
      }
    },

    closeVerifyDialog({ state }) {
      state.verifyDialog.visible = false
    },

    updateAdvertiserName({ state }, advertiser_name = '') {
      state.advertiser_name = advertiser_name
    },

    openDialog({ state, dispatch }, { type = 'add', id, exists = '' } = {}) {
      state.type = type
      state.exists = exists
      state.dialog = {
        ...state.dialog,
        visible: true,
        title: state.type === 'edit' ? '编辑广告主' : '新增广告主',
      }
      if (_.isNumber(id)) {
        state.info.id = id
        dispatch('getAdvertiserInfo', id)
      }
      dispatch('getOptions')
    },

    closeDialog({ state }) {
      state.dialog.visible = false
    },

    updateInfo({ state }, info: AdvertiserInfoState) {
      state.info = {
        id: info.id,
        basic: info.basic,
        expand: info.expand,
        invoices: info.invoices,
      }

      state.advertiser_email = info.basic.email
    },

    resetInfo({ state }) {
      state.info = getInfo()
    },

    addInvoice({ state }) {
      const invoices = state.info.invoices
      invoices.push({
        ...getInvoice(),
        name: state.info.basic.name
          ? `${state.info.basic.name}-${invoices.length + 1}`
          : '',
      })
      state.info.invoices = invoices
    },

    removeInvoice({ state }, { index } = {}) {
      const invoices = state.info.invoices
      invoices.splice(index, 1)
      state.info.invoices = invoices
    },

    async getAdvertiserInfo({ dispatch }, id) {
      const res = await fetchAdvertiserInfo({ id })
      const data = _.get(res, 'data.data.info')
      const basic: AdvertiserBasicInfoState = {
        ..._.pick(data, [
          'name',
          'email',
          'contact_num',
          'contact',
          'organization',
          'timezone',
          'country',
          'company_address',
          'remark',
          'mws_account_type',
          'cc_email',
        ]),
      }

      basic.cc_email = data.cc_email ? data.cc_email.join(',') : ''

      const expand: AdvertiserExpandInfoState = {
        password: data.password || '',
        bd_id: data.bd_id || '',
        bd_am_id: data.bd_am_id || '',
        pm_adv_id: data.pm_adv_id || '',
        ..._.pick(data, [
          'account_type',
          'currency',
          'cooperation_entity',
          'advance',
        ]),
      }
      const invoices: AdvertiserInvoice[] = data.invoices || [
        {
          name: '',
          company: '',
          address: '',
          readonly: false,
        },
      ]
      dispatch('updateInfo', {
        id,
        basic,
        expand,
        invoices,
      })
    },

    async create({ state }) {
      return createAdvertiser({
        ...state.info.basic,
        ...state.info.expand,
        invoices: state.info.invoices,
        bd_id: Number(state.info.expand.bd_id),
        bd_am_id: Number(state.info.expand.bd_am_id),
        pm_adv_id: Number(state.info.expand.pm_adv_id),
        cooperation_entity: Number(state.info.expand.cooperation_entity),
        cc_email: state.info.basic.cc_email.split(',').filter((t) => t),
      })
    },

    async update({ state }) {
      return updateAdvertiser(Number(state.info.id), {
        ...state.info.basic,
        ...state.info.expand,
        bd_id: Number(state.info.expand.bd_id),
        bd_am_id: Number(state.info.expand.bd_am_id),
        pm_adv_id: Number(state.info.expand.pm_adv_id),
        cooperation_entity: Number(state.info.expand.cooperation_entity),
        // 需过滤只读的
        invoices: (state.info.invoices || [])
          .filter((item) => !item.readonly)
          .map((item) => ({
            id: item.id,
            name: item.name,
            company: item.company,
            address: item.address,
          })),
        cc_email: state.info.basic.cc_email.split(',').filter((t) => t),
      })
    },
  },
}

export default advertiser
